// Logica pura del Inicio del portal (PP-64): que mostrar arriba para que el
// operador vea lo que hay que hacer y si alcanzan los moviles, en todas las
// plantas o en la filtrada. Sin React ni reloj ambiente: `nowMs` llega por
// parametro (regla de frontend).
//
// Las filas vienen de las lecturas multiplanta (ADR-025). La API ya resuelve
// las plantas de la cuenta; aca se repite como defensa: una fila de una
// planta o de un proveedor que la cuenta no tiene habilitado no se pinta.
import type { Driver, ProviderLoadSlot, ProviderMultiSiteTrip, ProviderSiteLoad, ProviderTrip, SiteVehicle } from "@ucorp/contracts";
import type { SiteAccess } from "../sites/port.js";
import { daysBetween } from "./documents.js";
import type { Expiries, ExpiryEntry } from "./expiries.js";
import { formatClock, tripActions } from "./requests.js";

type Access = Pick<SiteAccess, "siteId" | "providerId">;

/** Clave de una habilitacion: la empresa puede estar en una planta por dos vias. */
export function accessKey(access: Access): string {
  return `${access.siteId}:${access.providerId}`;
}

/** Filas de las habilitaciones en alcance (todas, o las de la planta filtrada). */
export function rowsInScope(rows: readonly ProviderMultiSiteTrip[], accesses: readonly Access[]): ProviderMultiSiteTrip[] {
  const keys = new Set(accesses.map(accessKey));
  return rows.filter((row) => keys.has(accessKey(row)));
}

/** Carga de las plantas en alcance. La carga es por planta, no por habilitacion. */
export function loadsInScope(loads: readonly ProviderSiteLoad[], accesses: readonly Access[]): ProviderSiteLoad[] {
  const sites = new Set(accesses.map((a) => a.siteId));
  return loads.filter((load) => sites.has(load.siteId));
}

/** La fila multiplanta sin el nombre de la planta: lo que esperan la bandeja y AssignForm. */
export function asProviderTrip(row: ProviderMultiSiteTrip): ProviderTrip {
  const { siteName: _siteName, ...trip } = row;
  return trip;
}

/** Clave de una fila: el mismo tripId no se repite entre plantas, pero no se supone. */
export function rowKey(row: Pick<ProviderMultiSiteTrip, "siteId" | "tripId">): string {
  return `${row.siteId}:${row.tripId}`;
}

function byDeadline(a: ProviderMultiSiteTrip, b: ProviderMultiSiteTrip): number {
  const da = a.offer === undefined ? Number.POSITIVE_INFINITY : Date.parse(a.offer.deadlineAt);
  const db = b.offer === undefined ? Number.POSITIVE_INFINITY : Date.parse(b.offer.deadlineAt);
  if (da !== db) return da - db;
  if (a.siteName !== b.siteName) return a.siteName < b.siteName ? -1 : 1;
  return a.tripId < b.tripId ? -1 : a.tripId > b.tripId ? 1 : 0;
}

/** Ofertas para responder de todas las plantas, la que vence primero arriba. */
export function offersToRespond(rows: readonly ProviderMultiSiteTrip[]): ProviderMultiSiteTrip[] {
  return rows.filter((row) => row.assignmentStatus === "offered" && row.offer?.state === "offered").sort(byDeadline);
}

/** Aceptados que esperan vehiculo y conductor, el reloj mas corto arriba. */
export function acceptedToAssign(rows: readonly ProviderMultiSiteTrip[]): ProviderMultiSiteTrip[] {
  return rows.filter((row) => row.assignmentStatus === "accepted" && row.offer?.state === "accepted").sort(byDeadline);
}

/** Lo que espera una accion del proveedor con el reloj todavia corriendo (para el titulo de la pestana). */
export function pendingNow(rows: readonly ProviderMultiSiteTrip[], nowMs: number): number {
  return rows.filter((row) => {
    const actions = tripActions(asProviderTrip(row), nowMs);
    return actions.respond || actions.assign;
  }).length;
}

/** "(3) Portal" con pendientes; el titulo original sin ellos. */
export function pendingTitle(base: string, pending: number): string {
  return pending > 0 ? `(${String(pending)}) ${base}` : base;
}

export interface DepartureGroup {
  /** "07:00" en la zona de la planta, o "Sin horario" si el viaje no tiene paradas. */
  label: string;
  rows: ProviderMultiSiteTrip[];
}

/**
 * Salidas del dia agrupadas por hora, en orden de salida. La hora es la local
 * de cada planta (regla 9): con plantas en zonas distintas, dos grupos con la
 * misma etiqueta pueden no ser el mismo instante, y se ordena por instante.
 */
export function departureGroups(rows: readonly ProviderMultiSiteTrip[], timezones: ReadonlyMap<string, string>): DepartureGroup[] {
  const timed = rows.filter((row) => row.summary?.departureAt !== undefined);
  const untimed = rows.filter((row) => row.summary?.departureAt === undefined);
  timed.sort((a, b) => {
    const diff = Date.parse(a.summary?.departureAt ?? "") - Date.parse(b.summary?.departureAt ?? "");
    return diff !== 0 ? diff : byDeadline(a, b);
  });
  const groups: DepartureGroup[] = [];
  for (const row of timed) {
    const timezone = timezones.get(row.siteId);
    const clock = timezone === undefined ? "" : formatClock(row.summary?.departureAt ?? "", timezone);
    const label = clock === "" ? "Sin horario" : `${clock.slice(0, 2)}:00`;
    const last = groups[groups.length - 1];
    if (last !== undefined && last.label === label) last.rows.push(row);
    else groups.push({ label, rows: [row] });
  }
  if (untimed.length > 0) groups.push({ label: "Sin horario", rows: untimed.sort(byDeadline) });
  return groups;
}

/**
 * Franja donde los viajes sin vehiculo superan a los vehiculos sin viaje
 * asignado en la franja. Los viajes ya asignados no cuentan: su vehiculo ya
 * no figura como libre, compararlos de nuevo los contaria dos veces.
 */
export function overloaded(slot: ProviderLoadSlot): boolean {
  return slot.unassignedTripCount > slot.freeVehicleCount;
}

/** Franjas con viajes: las vacias no le dicen nada al operador. */
export function busySlots(load: ProviderSiteLoad): ProviderLoadSlot[] {
  return load.slots.filter((slot) => slot.tripCount > 0);
}

/** La franja ya termino (su fin es excluido). */
export function slotPast(slot: ProviderLoadSlot, nowMs: number): boolean {
  const end = Date.parse(slot.endAt);
  return !Number.isNaN(end) && end <= nowMs;
}

// --- Aviso de oferta nueva ------------------------------------------------------

/** Clave de una oferta concreta: una reoferta del mismo viaje es otra oferta y vuelve a avisar. */
export function offerKey(row: ProviderMultiSiteTrip): string | undefined {
  if (row.offer === undefined) return undefined;
  return `${row.siteId}:${row.tripId}:${String(row.offer.offerId)}`;
}

/** Claves de las ofertas por responder. Las aceptadas no avisan: ya las tomo la empresa. */
export function liveOfferKeys(rows: readonly ProviderMultiSiteTrip[]): string[] {
  return rows
    .filter((row) => row.offer?.state === "offered")
    .map(offerKey)
    .filter((key): key is string => key !== undefined);
}

/**
 * Ofertas que no estaban en la vuelta anterior. Sin vuelta anterior (primera
 * carga, recarga de la pagina) no hay nada nuevo: lo que ya estaba no suena.
 */
export function freshOffers(seen: ReadonlySet<string> | undefined, rows: readonly ProviderMultiSiteTrip[]): string[] {
  if (seen === undefined) return [];
  return liveOfferKeys(rows).filter((key) => !seen.has(key));
}

// --- Avisos ---------------------------------------------------------------------

/** Ofertas de la vuelta anterior que ya no vienen. */
export function vanishedOffers(previous: readonly ProviderMultiSiteTrip[], current: readonly ProviderMultiSiteTrip[]): ProviderMultiSiteTrip[] {
  const still = new Set(current.map(rowKey));
  return previous.filter((row) => !still.has(rowKey(row)));
}

/**
 * De las ofertas que dejaron de venir, las que salieron de la bandeja: vencio
 * el tiempo y pasaron a otro proveedor, o la planta las cancelo. No salio la
 * que la empresa rechazo recien (ya se dijo) ni la que sigue siendo de la
 * empresa (otro operador la asigno). `found` es la lectura de los viajes de
 * esos dias; si un viaje vuelve a la cola sin oferta, tampoco es de la empresa.
 */
export function departedTrips(
  vanished: readonly ProviderMultiSiteTrip[],
  found: readonly ProviderMultiSiteTrip[],
  rejected: ReadonlySet<string>,
): ProviderMultiSiteTrip[] {
  const ours = new Set(found.filter((row) => row.assignmentStatus !== "unoffered").map(rowKey));
  return vanished.filter((row) => !rejected.has(rowKey(row)) && !ours.has(rowKey(row)));
}

export interface SiteRoster {
  vehicles: readonly SiteVehicle[];
  drivers: readonly Driver[];
}

export interface AssignmentWarning {
  row: ProviderMultiSiteTrip;
  /** Presente si el vehiculo asignado no es elegible en la planta. */
  vehicle?: SiteVehicle;
  /** Presente si el conductor asignado no es elegible en la planta. */
  driver?: Driver;
}

/**
 * Viajes asignados con un vehiculo o un conductor que hoy no es elegible en
 * la planta (ADR-014 §2: se puede asignar igual y la planta recibe la
 * alerta). La elegibilidad es la que calculo la API con la nomina de la
 * planta; sin nomina cargada no se adivina.
 */
export function assignmentWarnings(
  rows: readonly ProviderMultiSiteTrip[],
  rosters: ReadonlyMap<string, SiteRoster>,
): AssignmentWarning[] {
  const warnings: AssignmentWarning[] = [];
  for (const row of rows) {
    if (row.assignmentStatus !== "assigned" || row.status !== "scheduled") continue;
    const roster = rosters.get(accessKey(row));
    if (roster === undefined) continue;
    const vehicle = roster.vehicles.find((v) => v.id === row.vehicleId);
    const driver = roster.drivers.find((d) => d.id === row.driverId);
    const badVehicle = vehicle !== undefined && !vehicle.eligibility.eligible;
    const badDriver = driver !== undefined && !driver.eligibility.eligible;
    if (badVehicle || badDriver) {
      warnings.push({ row, ...(badVehicle ? { vehicle } : {}), ...(badDriver ? { driver } : {}) });
    }
  }
  return warnings;
}

/** Dias que mira el aviso de documentos del Inicio: lo vencido y lo que vence esta semana. */
export const HOME_EXPIRY_DAYS = 7;

/** Documentos vencidos o que vencen en los proximos `days` dias (fecha operativa de la planta). */
export function expiringSoon(expiries: Expiries, days: number = HOME_EXPIRY_DAYS): ExpiryEntry[] {
  const soon = expiries.expiringSoon.filter(
    (entry) => entry.expiresAt !== null && daysBetween(entry.today, entry.expiresAt) <= days,
  );
  return [...expiries.expired, ...soon];
}

// --- Inicio re-priorizado (rediseño, docs/PLAN-REDISENO-PORTAL.md §6.3) -----

/** Items por lista de accion del Inicio ("Para responder ahora", "Falta asignar"). El resto, en Viajes. */
export const HOME_ACTION_ROWS = 5;
/** Items por bloque informativo del Inicio (salidas, avisos). */
export const HOME_INFO_ROWS = 3;

/** Los primeros `n` y cuantos quedan afuera, para el "Ver más". */
export function topN<T>(rows: readonly T[], n: number): { shown: T[]; rest: number } {
  return { shown: rows.slice(0, n), rest: Math.max(0, rows.length - n) };
}

export interface NextSlot {
  siteId: string;
  siteName: string;
  slot: ProviderLoadSlot;
  /** Fin de la franja en la zona de la planta ("15:30"), o "" sin zona. */
  end: string;
  short: boolean;
  eligibleVehicleCount: number;
}

/**
 * La proxima franja con viajes de cada planta: la que esta corriendo o, si
 * ninguna, la primera que viene. Las pasadas no cuentan. Es lo que el
 * operador mira en el pico: si le alcanzan los moviles ahora.
 */
export function nextSlots(loads: readonly ProviderSiteLoad[], nowMs: number): NextSlot[] {
  const out: NextSlot[] = [];
  for (const load of loads) {
    const slot = busySlots(load).find((candidate) => !slotPast(candidate, nowMs));
    if (slot === undefined) continue;
    out.push({
      siteId: load.siteId,
      siteName: load.siteName,
      slot,
      end: formatClock(slot.endAt, load.timezone),
      short: overloaded(slot),
      eligibleVehicleCount: load.eligibleVehicleCount,
    });
  }
  return out;
}

/** Salidas que todavia no salieron y las que ya salieron, con la hora de salida como criterio. */
export function splitDepartures(rows: readonly ProviderMultiSiteTrip[], nowMs: number): { upcoming: ProviderMultiSiteTrip[]; past: ProviderMultiSiteTrip[] } {
  const upcoming: ProviderMultiSiteTrip[] = [];
  const past: ProviderMultiSiteTrip[] = [];
  for (const row of rows) {
    const departure = row.summary?.departureAt === undefined ? Number.NaN : Date.parse(row.summary.departureAt);
    if (!Number.isNaN(departure) && departure < nowMs) past.push(row);
    else upcoming.push(row);
  }
  return { upcoming, past };
}

/** Documentos que vencen hoy o ya vencieron: los que pueden dejar un movil no elegible en el dia. */
export function expiringToday(entries: readonly ExpiryEntry[]): ExpiryEntry[] {
  return entries.filter((entry) => entry.expiresAt !== null && daysBetween(entry.today, entry.expiresAt) <= 0);
}
